import React from 'react'

import styled from 'theme/styled-components'

import useI18n from 'i18n/useI18n'

import useTheme from 'theme/useTheme'

import Icons from 'components/icons/Icons'

import useReducer from 'store/useReducer'
import * as userStore from 'store/user/user'
import * as siteStore from 'sites/store/siteStore'

import api from '../api'
import Toast from 'components/toast/Toast'
import ToastContent from 'components/toast/ToastContent'

import Loader from 'react-loader-spinner'
import Logger from 'utils/Logger'

const COMMENT_MAX_LENGTH = 255

interface Props {
  incidentFcrId?: string
  comments?: CommentV6[]
  closed: boolean
  incidentType?: 'DI' | 'FLEET'
  onNewMessage?: () => void
}

const InterventionDetailChat = ({ incidentFcrId, comments, closed, incidentType, onNewMessage }: Props) => {
  const i18n = useI18n()
  const [theme] = useTheme()

  const currentSite = useReducer(siteStore.store, (s) => s.site)
  const user = useReducer(userStore.store, (s) => s.user)

  const [message, setMessage] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)

  const [localComments, setLocalComments] = React.useState<CommentV6[]>(comments || [])

  const scrollRef = React.useRef<HTMLDivElement>(null)

  const sendMessage = () => {
    if (!message || !currentSite || !user || !incidentFcrId || isLoading) return
    setIsLoading(true)
    api
      .postComment(i18n.lang, currentSite.id, incidentFcrId, user.type, encodeURI(message))
      .then(() => {
        api
          .getMyInterventions(currentSite.id, user.type, incidentType)
          .then((res) => {
            const myIntervention = res.incidents.find((intervention) => intervention.incidentNumber === incidentFcrId)
            if (myIntervention) {
              setLocalComments(myIntervention.comments || [])
            }
          })
          .catch((error) => {
            Logger.error('InterventionDetailChat', 'fetchComments', error)
            setLocalComments([
              ...localComments,
              {
                author: 'Living@ Interface',
                alreadyRead: true,
                comment: message,
                typeOfAuthor: 'USER',
                serviceNowCreationDate: new Date().toISOString(),
              },
            ])
          })
          .finally(() => {
            Toast.open(
              () =>
                ToastContent(i18n.t('screens.incident.chatWithTechnician.messageSent'), theme.colors.toastSuccessText),
              'top'
            )
            setMessage('')
            setIsLoading(false)
          })
      })
      .catch((error) => {
        setIsLoading(false)
        Logger.error('InterventionDetailChat', 'sendMessage', error)
        Toast.open(
          () => ToastContent(i18n.t('screens.incident.chatWithTechnician.messageError'), theme.colors.toastErrorText),
          'top'
        )
      })
  }

  const firstOfTheDay = (index: number) =>
    index === 0 ||
    new Date(sortedComments?.[index].serviceNowCreationDate ?? 0).getDay() !==
      new Date(sortedComments?.[index - 1]?.serviceNowCreationDate ?? 0).getDay()

  const sortedComments = React.useMemo(
    () =>
      localComments
        ?.sort((a, b) => new Date(a.serviceNowCreationDate).getTime() - new Date(b.serviceNowCreationDate).getTime())
        .map((comment) => ({
          ...comment,
          comment: comment.comment.replace('[code]<i>', '').replace('</i>[/code]', ''),
        })),
    [localComments?.length]
  )

  React.useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
    onNewMessage?.()
  }, [scrollRef.current, sortedComments?.length])

  return (
    <Container>
      <ChatContainer ref={scrollRef}>
        {sortedComments?.length === 0 && (
          <MessageInformations>{i18n.t('screens.incident.chatWithTechnician.noMessage')}</MessageInformations>
        )}
        {sortedComments?.map((comment, index) => (
          <MessageContainer key={index} role={comment.typeOfAuthor}>
            {firstOfTheDay(index) && (
              <MessageInformations>
                {`${i18n.t(`screens.incident.chatWithTechnician.${comment.typeOfAuthor}`, {
                  name: comment.author,
                })} - ${i18n.t('screens.incident.chatWithTechnician.messageDate', {
                  date: new Date(comment.serviceNowCreationDate),
                })}`}
              </MessageInformations>
            )}
            <Message role={comment.typeOfAuthor}>{comment.comment}</Message>
            <MessageTime role={comment.typeOfAuthor}>
              {i18n.t('screens.incident.chatWithTechnician.timeSent', {
                date: new Date(comment.serviceNowCreationDate),
              })}
            </MessageTime>
          </MessageContainer>
        ))}
      </ChatContainer>
      <MessageBar
        action="submit"
        onSubmit={(e) => {
          e.preventDefault()
          sendMessage()
        }}>
        <MessageInput
          disabled={closed}
          placeholder={
            closed
              ? i18n.t('screens.incident.chatWithTechnician.closed')
              : i18n.t('screens.incident.chatWithTechnician.placeholder')
          }
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <RightContainer>
          <SendButton disabled={closed || message.length > COMMENT_MAX_LENGTH} type="submit">
            {isLoading ? (
              <Loader type="TailSpin" height={22} width={22} color={theme.colors.white} />
            ) : (
              <Icons name="send" size={22} color={theme.colors.white} />
            )}
          </SendButton>
          <CharCount size={message.length}>
            {message.length}/{COMMENT_MAX_LENGTH}
          </CharCount>
        </RightContainer>
      </MessageBar>
    </Container>
  )
}

export default InterventionDetailChat

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ChatContainer = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumLightGrey};
  padding: 5px 10px;
  max-height: 400px;
  overflow-y: auto;
`

const MessageInformations = styled.p`
  ${(props) => props.theme.fonts.label};
  color: ${(props) => props.theme.colors.darkGrey};
  text-align: center;
  margin: 8px auto 4px;
`

const MessageContainer = styled.div<{ role: MessageUserRole }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.role === 'USER' ? 'flex-end' : 'flex-start')};
  margin: 1px 0;
`

const Message = styled.p<{ role: MessageUserRole }>`
  ${(props) => props.theme.fonts.label};
  background-color: ${(props) => (props.role === 'USER' ? props.theme.colors.blue : props.theme.colors.white)};
  color: ${(props) => (props.role === 'USER' ? props.theme.colors.white : props.theme.colors.black)};
  padding: 10px;
  width: fit-content;
  max-width: 80%;
  margin: ${(props) => (props.role === 'USER' ? '0 0 0 auto' : '0 auto 0 0')};
  overflow-wrap: break-word;
`

const MessageTime = styled.p<{ role: MessageUserRole }>`
  ${(props) => props.theme.fonts.label};
  font-size: 10px;
  color: ${(props) => props.theme.colors.darkGrey};
  width: 100%;
  text-align: ${(props) => (props.role === 'USER' ? 'right' : 'left')};
  margin: 0 1px;
`

const MessageBar = styled.form`
  display: flex;
  flex: 1;
  flex-direction: row;
  border: 1px solid ${({ theme }) => theme.colors.mediumLightGrey};
`

const MessageInput = styled.textarea`
  flex: 1;
  padding: 10px;
  height: 40px;
  border: none;
  resize: none;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: auto 14px;
`

const SendButton = styled.button<{ disabled: boolean }>`
  background-color: ${(props) => (props.disabled ? props.theme.colors.mediumLightGrey : props.theme.colors.blue)};
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CharCount = styled.p<{ size: number }>`
  ${(props) => props.theme.fonts.label};
  color: ${(props) => (props.size > COMMENT_MAX_LENGTH ? props.theme.colors.red : props.theme.colors.placeholderGrey)};
  font-size: 10px;
  margin: 0;
  text-align: center;
`
